import injectScript, { injectCss } from './utils/injectScript';
import { getSocialNetwork } from './utils/getSocialNetwork';
import { APPLE_CLIENT_ID, ERROR_MESSAGES, SOCIAL_NETWORK_NAMES } from './constants';
import { LoadPayload, WidgetParams } from './types';
import serverVars from 'server-vars';

type LoaderScripts = {
    [key in LoadPayload]: (options?: WidgetParams) => Promise<void>;
};

const scripts: LoaderScripts = {
    async pinterest(options) {
        if (!options || !options.relayEnvironment) {
            throw new Error(ERROR_MESSAGES.PINTEREST_OPTIONS_NOT_PROVIDED);
        }
        const pinterestApp = await getSocialNetwork.byName('PINTEREST', options.relayEnvironment);
        if (!pinterestApp) {
            throw new Error(ERROR_MESSAGES.PINTEREST_APP_NOT_AVAILABLE);
        }
        await injectScript('//assets.pinterest.com/sdk/sdk.js');
    },

    async facebook(options) {
        if (!options || !options.relayEnvironment) {
            throw new Error(ERROR_MESSAGES.FACEBOOK_OPTIONS_NOT_PROVIDED);
        }
        const locale = options.locale || 'en-US';
        const formattedLocale = locale.replace('-', '_'); // locale must be snake_case
        const facebookApp = await getSocialNetwork.byName(
            SOCIAL_NETWORK_NAMES.FACEBOOK,
            options.relayEnvironment
        );
        if (!facebookApp) {
            throw new Error(ERROR_MESSAGES.FACEBOOK_APP_NOT_AVAILABLE);
        }
        try {
            await injectScript(`//connect.facebook.net/${formattedLocale}/sdk.js`, {
                id: 'facebook-jssdk',
            });
        } catch (err) {
            throw new Error(
                `${SOCIAL_NETWORK_NAMES.FACEBOOK} ${ERROR_MESSAGES.SOCIAL_NETWORK_SDK_LOADING_FAILED}`
            );
        }
        if (!facebookApp.parameters || !facebookApp.parameters.clientId) {
            throw new Error(ERROR_MESSAGES.MISSING_SOCIAL_NETWORK_PARAMETERS);
        }
        global.FB.init({
            appId: facebookApp.parameters.clientId,
            autoLogAppEvents: true,
            xfbml: true,
            version: 'v3.2',
        });
        window.FB.AppEvents.logPageView();
    },

    async google(options) {
        if (!options || !options.relayEnvironment) {
            throw new Error(ERROR_MESSAGES.GOOGLE_OPTIONS_NOT_PROVIDED);
        }
        const googleApp = await getSocialNetwork.byName(
            SOCIAL_NETWORK_NAMES.GOOGLE,
            options.relayEnvironment
        );
        if (!googleApp) {
            throw new Error(ERROR_MESSAGES.GOOGLE_APP_NOT_AVAILABLE);
        }
        try {
            await injectScript('//apis.google.com/js/platform.js?onload=init');
        } catch (err) {
            throw new Error(
                `${SOCIAL_NETWORK_NAMES.GOOGLE} ${ERROR_MESSAGES.SOCIAL_NETWORK_SDK_LOADING_FAILED}`
            );
        }
        return new Promise((resolve, reject) => {
            window.gapi.load('auth2', () => {
                if (!googleApp.parameters || !googleApp.parameters.clientId) {
                    reject(ERROR_MESSAGES.MISSING_SOCIAL_NETWORK_PARAMETERS);
                    return;
                }
                window.gapi.auth2.init({ client_id: googleApp.parameters.clientId }).then(
                    () => {
                        resolve();
                    },
                    error => {
                        if (error.details === 'Cookies are not enabled in current environment.') {
                            reject(new Error(ERROR_MESSAGES.THIRD_PARTY_COOKIES_DISABLED));
                        } else {
                            reject(error);
                        }
                    }
                );
            });
        });
    },

    async googleOneTap(options) {
        if (!options || !options.relayEnvironment) {
            throw new Error(ERROR_MESSAGES.GOOGLE_OPTIONS_NOT_PROVIDED);
        }
        const googleApp = await getSocialNetwork.byName('GOOGLE', options.relayEnvironment);
        if (!googleApp) {
            throw new Error(ERROR_MESSAGES.GOOGLE_APP_NOT_AVAILABLE);
        }
        if (!googleApp.parameters || !googleApp.parameters.clientId) {
            throw new Error(ERROR_MESSAGES.MISSING_SOCIAL_NETWORK_PARAMETERS);
        }
        await injectScript('//accounts.google.com/gsi/client');
        window.dibsGoogleOneTapCallbacks = window.dibsGoogleOneTapCallbacks || [];
        window.google.accounts.id.initialize({
            client_id: googleApp.parameters.clientId,
            cancel_on_tap_outside: false,
            callback: response => window.dibsGoogleOneTapCallbacks.forEach(fn => fn(response)),
        });
    },

    async apple(options) {
        if (!options || !options.relayEnvironment) {
            throw new Error(ERROR_MESSAGES.APPLE_OPTIONS_NOT_PROVIDED);
        }
        const appleApp = await getSocialNetwork.byName(
            SOCIAL_NETWORK_NAMES.APPLE,
            options.relayEnvironment
        );
        if (!appleApp) {
            throw new Error(ERROR_MESSAGES.APPLE_APP_NOT_AVAILABLE);
        }
        try {
            await injectScript(
                '//appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
            );
        } catch (err) {
            throw new Error(
                `${SOCIAL_NETWORK_NAMES.APPLE} ${ERROR_MESSAGES.SOCIAL_NETWORK_SDK_LOADING_FAILED}`
            );
        }
        window.AppleID.auth.init({
            clientId: APPLE_CLIENT_ID,
            scope: 'email name',
            redirectURI: `${serverVars.get('localeOrigin')}/signinwithapple/dibs-post`,
            state: '',
            usePopup: true,
        });
    },

    async recaptcha() {
        await injectScript('//www.google.com/recaptcha/api.js?render=explicit');
    },

    async recaptchaEnterprise({ recaptchaV3SiteKey } = {}) {
        await injectScript(`//www.google.com/recaptcha/enterprise.js?render=${recaptchaV3SiteKey}`);
    },
    async okta(options) {
        if (!options || !options.relayEnvironment) {
            throw new Error(ERROR_MESSAGES.GOOGLE_OPTIONS_NOT_PROVIDED);
        }

        const oktaConfig = await getSocialNetwork.byName('OKTA', options.relayEnvironment);

        if (!oktaConfig) {
            throw new Error(ERROR_MESSAGES.OKTA_APP_NOT_AVAILABLE);
        }

        if (!oktaConfig.parameters || !oktaConfig.parameters.clientId) {
            throw new Error(ERROR_MESSAGES.MISSING_SOCIAL_NETWORK_PARAMETERS);
        }

        await injectScript('//global.oktacdn.com/okta-signin-widget/5.11.1/js/okta-sign-in.min.js');
        injectCss('https://global.oktacdn.com/okta-signin-widget/5.9.4/css/okta-sign-in.min.css');

        window.OktaWidget = new window.OktaSignIn({
            baseUrl: 'https://okta.1stdibs.com',
            clientId: oktaConfig.parameters.clientId,
            redirectUri: `${window.location.origin}/internal`,
            helpSupportNumber: null,
            logo: 'https://a.1stdibscdn.com/dist/adhoc/logo/1stdibs-logo.png',
            language: 'en',
            i18n: {
                en: {
                    'primaryauth.submit': 'Log In',
                    'primaryauth.title': 'Log in using your Okta credentials.',
                },
            },
            authParams: {
                issuer: 'https://okta.1stdibs.com',
                pkce: false,
                responseType: 'token',
            },
            features: {
                registration: false,
                autoPush: true,
                showPasswordToggleOnSignInPage: true,
                idpDiscovery: true,
            },
            idpDiscovery: {
                requestContext: window.location.href,
            },
            colors: {
                brand: '#000000',
            },
        });
    },
};

const cachedLoaders: Partial<Record<keyof LoaderScripts, Promise<void>>> = {};

/**
 * Scripts loader. Also it caches loading promise to load script once.
 */
const load = function (type: keyof LoaderScripts, options?: WidgetParams): Promise<void> {
    let loader = cachedLoaders[type];
    if (loader) {
        return loader;
    }

    if (!scripts[type]) {
        throw new Error('Widget script for ' + type + ' is not provided.');
    }

    loader = scripts[type](options);
    cachedLoaders[type] = loader;

    return loader;
};

const widgetLoader = {
    load,
};

export default widgetLoader;

export * from './types';
